import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import Sidebar from '../Sidebar';
import { Session } from "../utils/session";
import UserAvatar from '../UserAvatar';


const authHeaders = () => {
  const token = Session.getToken();
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`
  };
};

function mapUserToForm(user) {
  if (!user) return null;
  return {
    UserId: user.userId ?? user.UserId,
    UserName: user.userName ?? user.UserName ?? '',
    FirstName: user.firstName ?? user.FirstName ?? '',
    LastName: user.lastName ?? user.LastName ?? '',
    Email: user.email ?? user.Email ?? '',
    PhoneNo: user.phoneNo ?? user.PhoneNo ?? '',
    UserRoleId: String(user.userRoleId ?? user.UserRoleId ?? ''),
    CompaniesId: String(user.companiesId ?? user.CompaniesId ?? ''),
    IsActive: user.isActive ?? user.IsActive ?? true,
    TravellingUser: !!(user.travellingUser ?? user.TravellingUser),
    AvatarBase64: user.avatarBase64 ?? user.AvatarBase64 ?? '',
  };
}
export default function MembersEdit({ profileMode = false }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const { state, pathname } = useLocation();
  const isProfileMode = profileMode || /my-profile/i.test(pathname);
  const sessionUserId = Session.getUserId();
  const effectiveId = isProfileMode ? String(sessionUserId || '') : id;
  const backPath = isProfileMode ? '/dailytask' : '/members';
  const canEditIsActive = Session.isAdmin();
  const canEditTravellingUser = Session.isAdmin() && !isProfileMode;
  const canEditRole = !isProfileMode;

  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [hideDesktop, setHideDesktop] = useState(false);
  const [roles, setRoles] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [imageChanged, setImageChanged] = useState(false);
  const [originalIsActive, setOriginalIsActive] = useState(true);
  const [originalTravellingUser, setOriginalTravellingUser] = useState(false);
  const [originalUserRoleId, setOriginalUserRoleId] = useState('');

  const [form, setForm] = useState({
    UserId: effectiveId,
    UserName: '',
    FirstName: '',
    LastName: '',
    Email: '',
    PhoneNo: '',
    UserRoleId: '',
    CompaniesId: '',
    IsActive: true,
    TravellingUser: false,
    AvatarBase64: '',
    Password: ''
  });


  const toggleSidebar = () => {
    if (window.innerWidth <= 991) {
      setShowMobileSidebar(s => !s);
    } else {
      setHideDesktop(s => !s);
    }
  };

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 991) setShowMobileSidebar(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Sidebar logic - useEffect for direct DOM manipulation
  useEffect(() => {
    const overlay = document.getElementById('sidebar-overlay');
    if (overlay) {
      overlay.style.display = showMobileSidebar ? 'block' : 'none';
    }
    const sidebar = document.getElementById('sidebar');
    if (sidebar) {
      if (showMobileSidebar) sidebar.classList.add('show-mobile'); else sidebar.classList.remove('show-mobile');
      if (hideDesktop) sidebar.classList.add('hide-desktop'); else sidebar.classList.remove('hide-desktop');
    }
    const content = document.getElementById('content');
    if (content) {
      if (hideDesktop) content.classList.add('full-width'); else content.classList.remove('full-width');
    }
  }, [showMobileSidebar, hideDesktop]);

  useEffect(() => {
    if (!isProfileMode && Session.getRole() !== 'Admin') {
      if (sessionUserId && Number(id) !== Number(sessionUserId)) {
        navigate('/my-profile', { replace: true });
      }
    }
  }, [id, isProfileMode, navigate, sessionUserId]);

  useEffect(() => {
    if (isProfileMode || !effectiveId) return;

    const loadMember = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_BASE_URL}/api/Members/${effectiveId}`, {
          headers: authHeaders(),
        });
        if (!res.ok) {
          console.error('Failed to load member:', res.status);
          return;
        }
        const user = await res.json();
        const mapped = mapUserToForm(user);
        if (!mapped) return;
        setForm((f) => ({ ...f, ...mapped }));
        setOriginalIsActive(mapped.IsActive ?? true);
        setOriginalTravellingUser(mapped.TravellingUser ?? false);
        setOriginalUserRoleId(mapped.UserRoleId ?? '');
        setImageChanged(false);
      } catch (err) {
        console.error('Failed to load member:', err);
      } finally {
        setLoading(false);
      }
    };

    loadMember();
  }, [isProfileMode, effectiveId]);

  useEffect(() => {
    if (state?.user && !isProfileMode) {
      setForm(f => ({
        ...f,
        UserId: state.user.UserId,
        UserName: state.user.UserName || '',
        FirstName: state.user.FirstName || '',
        LastName: state.user.LastName || '',
        Email: state.user.Email || '',
        PhoneNo: state.user.PhoneNo || '',
        UserRoleId: state.user.UserRoleId || '',
        CompaniesId: state.user.CompaniesId || '',
        IsActive: state.user.IsActive ?? true,
        TravellingUser: !!(state.user.TravellingUser ?? state.user.travellingUser),
      }));
      setOriginalIsActive(state.user.IsActive ?? true);
      setOriginalTravellingUser(!!(state.user.TravellingUser ?? state.user.travellingUser));
      setOriginalUserRoleId(String(state.user.UserRoleId || ''));
      setImageChanged(false);
    }
  }, [state, isProfileMode]);

  useEffect(() => {
    if (!isProfileMode || !sessionUserId) return;

    const loadProfile = async () => {
      try {
        setLoading(true);

        const applyUser = (user) => {
          const mapped = mapUserToForm(user);
          if (!mapped) return false;
          setForm((f) => ({ ...f, ...mapped }));
          setOriginalIsActive(mapped.IsActive ?? true);
          setOriginalTravellingUser(mapped.TravellingUser ?? false);
          setOriginalUserRoleId(mapped.UserRoleId ?? '');
          setImageChanged(false);
          return true;
        };

        const profileRes = await fetch(`${API_BASE_URL}/api/Members/profile`, {
          headers: authHeaders(),
        });
        if (profileRes.ok) {
          const user = await profileRes.json();
          if (applyUser(user)) return;
        }

        const userName = Session.getUserName();
        if (userName) {
          const byNameRes = await fetch(
            `${API_BASE_URL}/api/Members/by-username/${encodeURIComponent(userName)}`,
            { headers: authHeaders() }
          );
          if (byNameRes.ok) {
            const user = await byNameRes.json();
            if (applyUser(user)) return;
          }
        }

        console.error('Failed to load profile:', profileRes.status);
      } catch (err) {
        console.error('Failed to load profile:', err);
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [isProfileMode, sessionUserId]);

  useEffect(() => {
    const loadLookups = async () => {
      try {
        const [rolesRes, companiesRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/UserRole/roles`, { headers: authHeaders() }),
          fetch(`${API_BASE_URL}/api/UserRole/companies`, { headers: authHeaders() })
        ]);

        if (!rolesRes.ok) {
          console.error('Roles fetch failed', rolesRes.status, await rolesRes.text());
          throw new Error(`Roles request failed with status ${rolesRes.status}`);
        }
        if (!companiesRes.ok) {
          console.error('Companies fetch failed', companiesRes.status, await companiesRes.text());
          throw new Error(`Companies request failed with status ${companiesRes.status}`);
        }

        const rolesData = await rolesRes.json();
        const companiesData = await companiesRes.json();

        setRoles(rolesData || []);
        setCompanies(companiesData || []);
      } catch (err) {
        console.error('Failed to load lookups:', err);
      }
    };

    loadLookups();
  }, [isProfileMode]);

  useEffect(() => {
    const loadPassword = async () => {
      if (!effectiveId) return;

      try {
        const res = await fetch(`${API_BASE_URL}/api/Members/password/${effectiveId}`, {
          headers: authHeaders()
        });

        if (res.ok) {
          const data = await res.json();
          setForm(prev => ({
            ...prev,
            Password: data.password || data.Password || ''
          }));
        }
      } catch (err) {
        console.error('Failed to load password:', err);
      }
    };

    loadPassword();
  }, [effectiveId]);


  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert("Please select a valid image file.");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setForm(prev => ({
        ...prev,
        AvatarBase64: reader.result
      }));
      setImageChanged(true);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = { ...form };
      if (!imageChanged) {
        delete payload.AvatarBase64;
      }
      if (!canEditIsActive) {
        payload.IsActive = originalIsActive;
      }
      if (!canEditTravellingUser) {
        payload.TravellingUser = originalTravellingUser;
      }
      if (!canEditRole) {
        payload.UserRoleId = originalUserRoleId;
      }

      const res = await fetch(`${API_BASE_URL}/api/Members/update`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        alert(isProfileMode ? 'Profile updated successfully' : 'Member updated successfully');
        navigate(backPath);
      } else if (res.status === 400) {
        const data = await res.json().catch(() => ({}));
        alert(data.message || data.Message || 'Update failed');
      } else {
        const errorText = await res.text();
        alert('Update failed: ' + errorText);
      }
    } catch (err) {
      alert('Update failed: ' + err.message);
    }
  };

 return (
  <div>
    <Sidebar />

    <div id="content" className="p-4">
      {loading ? (
        <div
          className="d-flex align-items-center justify-content-center text-muted"
          style={{ minHeight: '70vh' }}
        >
          Loading...
        </div>
      ) : (
        <>
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div className="d-flex align-items-center gap-3">
          <h4 className="mb-0">{isProfileMode ? 'My Profile' : 'Edit Member'}</h4>
        </div>
      </div>

      {/* Card */}
      <div className="card shadow-sm border-0">
        <div className="card-body">

          <form onSubmit={handleSubmit} className="row g-4">

            {/* Username */}
            <div className="col-md-6">
              <label className="form-label fw-semibold">Username</label>
              <input
               className="form-control"
               name="UserName"
               value={form.UserName}
               onChange={handleChange}
               required
             />
            </div>

            {/* First Name */}
            <div className="col-md-6">
              <label className="form-label fw-semibold">First Name</label>
              <input
                className="form-control"
                name="FirstName"
                value={form.FirstName}
                onChange={handleChange}
                required
              />
            </div>

            {/* Last Name */}
            <div className="col-md-6">
              <label className="form-label fw-semibold">Last Name</label>
              <input
                className="form-control"
                name="LastName"
                value={form.LastName}
                onChange={handleChange}
                required
              />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">Email</label>
              <input
  type="email"
  className="form-control"
  name="Email"
  value={form.Email}
  onChange={handleChange}
  required
/>
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">Company</label>
              <select
                className="form-select"
                name="CompaniesId"
                value={form.CompaniesId}
                onChange={handleChange}
                required
              >
                <option value="">Select Company</option>
                {companies.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            {/* Role */}
            <div className="col-md-6">
              <label className={`form-label fw-semibold${!canEditRole ? ' text-muted' : ''}`}>Role</label>
              <select
                className="form-select"
                name="UserRoleId"
                value={form.UserRoleId}
                onChange={handleChange}
                required
                disabled={!canEditRole}
              >
                <option value="">Select Role</option>
                {roles.map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">Phone</label>
              <input
  type="tel"
  className="form-control"
  name="PhoneNo"
  value={form.PhoneNo}
  maxLength={10}
  pattern="[0-9]{10}"
  required
  onChange={(e) => {
    const value = e.target.value.replace(/\D/g, "");
    setForm(prev => ({
      ...prev,
      PhoneNo: value
    }));
  }}
/>
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">Password</label>
              <div className="position-relative">
                <input
                  className="form-control pe-5"
                  name="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={form.Password}
                  onChange={handleChange}
                  placeholder="Enter password"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="toggle-password-btn"
                  onClick={() => setShowPassword(s => !s)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <i className={showPassword ? 'fa-regular fa-eye-slash' : 'fa-regular fa-eye'} />
                </button>
              </div>
            </div>

            <div className="col-md-6 d-flex align-items-end">
              <div className="form-check mt-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="IsActive"
                  checked={form.IsActive}
                  onChange={handleChange}
                  disabled={!canEditIsActive}
                />
                <label className={`form-check-label fw-semibold${!canEditIsActive ? ' text-muted' : ''}`}>
                  Is Active
                </label>
              </div>
            </div>
            {canEditTravellingUser && (
              <div className="col-md-6 d-flex align-items-end">
                <div className="form-check mt-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    name="TravellingUser"
                    checked={form.TravellingUser}
                    onChange={handleChange}
                  />
                  <label className="form-check-label fw-semibold">
                    Travelling User
                  </label>
                </div>
              </div>
            )}
            <div className="col-md-6">
              <label className="form-label fw-semibold">Profile Image</label>
              <input
                type="file"
                className="form-control"
                accept="image/*"
                onChange={handleImage}
              />
            </div>

            {/* Image Preview */}
            <div className="col-md-6 text-center">
              <label className="form-label fw-semibold d-block">
                Current Image
              </label>
              <UserAvatar
                userId={form.UserId}
                userName={form.UserName}
                srcOverride={form.AvatarBase64 || undefined}
                className="rounded-circle shadow"
                style={{
                  width: 100,
                  height: 100,
                  objectFit: 'cover',
                  border: '3px solid #f1f1f1',
                }}
              />
            </div>

            {/* Buttons */}
            <div className="col-12 text-end mt-4">
              <button type="submit" className="btn btn-success px-4">
                Update
              </button>
              <button
                type="button"
                className="btn btn-secondary ms-2 px-4"
                onClick={() => navigate(backPath)}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
        </>
      )}
    </div>
  </div>
);

}
